import axios from 'axios';

// Configuração da API baseada no ambiente
const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7082/api';

// Criar instância do axios com configurações padrão
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 segundos
  // Para desenvolvimento com HTTPS auto-assinado, configure o browser para aceitar certificados
  withCredentials: false,
});

// Adicionar interceptor para lidar com erros de CORS/SSL
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ERR_CERT_AUTHORITY_INVALID' || error.message.includes('certificate')) {
      console.warn('Certificado SSL inválido detectado. Configure o browser para aceitar certificados auto-assinados.');
    }
    throw error;
  }
);

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de resposta para tratar erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Tratamento de timeout
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.warn('Timeout na requisição - servidor pode estar indisponível');
      // Aqui você pode implementar um modo de desenvolvimento ou fallback
      return Promise.reject(new Error('Tempo limite da requisição excedido'));
    }
    
    // Tratamento de servidor indisponível
    if (error.code === 'ERR_NETWORK' || !error.response) {
      console.warn('Servidor indisponível - ativando modo de desenvolvimento');
      // Configuração da API
      return Promise.reject(new Error('Servidor indisponível'));
    }
    
    if (error.response?.status === 401) {
      // Token inválido ou expirado
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Tipos de dados
export interface Professor {
  id?: number;
  nome: string;
}

export interface Turma {
  id?: string;
  nome: string;
  periodo?: {
    id: string;
    nome: string;
  };
}

export interface TurmaRequest {
  nome: string;
}

export interface Disciplina {
  id?: string;
  nome: string;
  cargaHoraria?: number;
  professorId?: string;
  // Segundo professor opcional - disciplina coensinada simultaneamente pelos 2.
  professorId2?: string;
  turmaId?: string;
  professor?: Professor;
  professor2?: Professor;
  turma?: Turma;
}

export interface DisciplinaRequest {
  nome: string;
  cargaHoraria: number;
  professorId: string;
  professorId2?: string;
  turmaId: string[];
}

export interface ProfessorDisciplina {
  professor: {
    id: string;
    nome: string;
  };
  disciplinas: Array<{
    id: string;
    nome: string;
    cargaHoraria: number;
    professor?: { id: string; nome: string };
    professor2?: { id: string; nome: string } | null;
    turma: {
      id: string;
      nome: string;
      periodo: {
        id: string;
        nome: string;
      };
    };
  }>;
}

export interface Periodo {
  id?: string;
  nome: string;
}

export interface Laboratorio {
  id?: string;
  nome: string;
}

export interface LaboratorioRequest {
  nome: string;
}

export type ModoLaboratorio = 'QUALQUER' | 'PREFERENCIAL' | 'EXCLUSIVO';

export interface LaboratorioDisciplina {
  id: string;
  quantidade: number;
  modo: ModoLaboratorio;
  // Null quando modo é QUALQUER (sem laboratório específico vinculado).
  laboratorioId: string | null;
  disciplina: {
    id: string;
    nome: string | null;
    cargaHoraria: number;
    professor: { id: string; nome: string | null };
    turmaId: string;
  };
}

export interface LaboratorioDisciplinaRequest {
  disciplinaId: string;
  quantidade: number;
  modo: ModoLaboratorio;
  // Obrigatório quando modo é PREFERENCIAL ou EXCLUSIVO; omitido/ignorado quando QUALQUER.
  laboratorioId?: string;
}

export interface Horario {
  id?: number | string;
  diaSemana: number;
  posicaoDia?: number;
  horaInicio?: string;
  horaFim?: string;
  inicio?: string;
  fim?: string;
  periodoId?: string;
}

export interface Agenda {
  id?: number;
  nome: string;
  professorId?: string;
  periodoId?: string;
  professor?: {
    id: string;
    nome: string;
  };
  periodo?: {
    id: string;
    nome: string;
  };
}

export interface AgendaRequest {
  nome: string;
  professorId: string;
  periodoId: string;
}

export interface Grade {
  id?: string;
  nome: string;
  tipo?: number;
  data?: string;
  horarios?: GradeHorario[];
}

export interface GradeLaboratorio {
  id: string;
  nome: string;
  data: string;
  gradeId: string;
  gradeHorarios: GradeLaboratorioHorario[];
}

export interface GradeLaboratorioHorario {
  id: string;
  turma: {
    id: string;
    nome: string;
  } | null;
  horario: {
    id: string;
    diaSemana: number;
    inicio: string;
    fim: string;
  };
  disciplina: {
    id: string;
    nome: string;
    professor: {
      id: string;
      nome: string;
    };
    professor2?: { id: string; nome: string } | null;
  } | null;
  laboratorio: {
    id: string;
    nome: string;
  };
  conflitoProfessorAgenda: boolean;
  conflitoHorario: boolean;
  bloqueado: boolean;
  alterado?: boolean; // Nova propriedade para marcar alterações via drag and drop
}

export interface GradeHorario {
  id: string;
  turma: {
    id: string;
    nome: string;
  };
  horario: {
    id: string;
    diaSemana: number;
    inicio: string;
    fim: string;
  };
  disciplina: {
    id: string;
    nome: string;
    professor: {
      id: string;
      nome: string;
    };
    professor2?: { id: string; nome: string } | null;
    turmaId: string;
  };
  conflitoProfessorAgenda?: boolean;
  conflitoHorario?: boolean;
  bloqueado?: boolean;
}

export interface GradeRequest {
  nome: string;
}

export interface SubstituirProfessorRequest {
  disciplinaId: string;
  professorAntigoId: string;
  professorNovoId: string;
  atualizarDisciplina?: boolean;
}

export interface SubstituirProfessorResponse {
  atualizados: number;
  avisos: string[];
}

export interface GradeGenerateResponse {
  taskId: string;
  status: string;
}

export interface DiagnosticoItem {
  tipo: string;
  mensagem: string;
  detalhes?: Record<string, unknown>;
}

export interface GradeStatusResponse {
  status: string;
  message?: string;
  gradeId?: string;
  diagnostico?: DiagnosticoItem[];
}

export interface SugestaoRemanejamento {
  tipo: 'troca_pronta' | 'precisa_liberar_agenda' | 'sem_solucao';
  mensagem: string;
  aulaSemLaboratorio: { gradeHorarioId: string; disciplina: string; turma: string; horario: string };
  trocaCom?: { gradeHorarioId: string; horarioDestino: string };
  bloqueios?: Array<{ tipo: 'professor' | 'turma'; nome: string; horario: string }>;
}

export interface GradeLaboratorioGerarResponse {
  id: string;
  diagnostico: DiagnosticoItem[];
  sugestoesRemanejamento: SugestaoRemanejamento[];
}

export interface AgendaProfessor {
  id?: string;
  horario: {
    id: string;
    diaSemana: number;
    inicio: string;
    fim: string;
    periodoId: string;
  };
  disponivel: boolean;
}

export interface AgendaProfessorRequest {
  disponivel: boolean;
}

export interface AgendaTurma {
  id?: string;
  nome: string;
  turma: {
    id: string;
    nome: string;
    periodo: {
      id: string;
      nome: string;
    };
  };
}

export interface AgendaTurmaHorario {
  id?: string;
  horario: {
    id: string;
    diaSemana: number;
    posicaoDia: number;
    inicio: string;
    fim: string;
    periodoId: string;
  };
  disponivel: boolean;
}

export interface AgendaTurmaHorarioRequest {
  disponivel: boolean;
}

export interface ConfiguracaoGrade {
  id: string;
  aulaGeminada: number;
  aulaGeminadaObrigatoria: boolean;
  quantidadeAulasPeriodo: number;
}

// Serviços da API
export const authService = {
  login: async (email: string, password: string) => {
    const response = await api.post('/Auth', { email, senha: password });
    return {
      token: response.data.token,
      usuario: response.data.usuario
    };
  },
  
  logout: async () => {
    try {
      await api.post('/Auth/logout');
    } catch (error) {
      // Ignorar erros de logout se o token já expirou
      console.warn('Erro no logout:', error);
    } finally {
      localStorage.removeItem('authToken');
    }
  },
};

export const professorService = {
  getAll: async (): Promise<Professor[]> => {
    const response = await api.get('/Professor/Listar');
    return response.data;
  },
  
  getById: async (id: number): Promise<Professor> => {
    const response = await api.get(`/Professor/${id}`);
    return response.data;
  },
  
  create: async (professor: Professor): Promise<Professor> => {
    const response = await api.post('/Professor/Cadastrar', professor);
    return response.data;
  },
  
  update: async (id: number, professor: Professor): Promise<Professor> => {
    const response = await api.put(`/Professor/${id}/Editar`, professor);
    return response.data;
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/Professor/${id}/Deletar`);
  },
};

export const turmaService = {
  getAll: async (): Promise<Turma[]> => {
    const response = await api.get('/Turma/Listar');
    return response.data;
  },
  
  getById: async (id: string): Promise<Turma> => {
    const response = await api.get(`/Turma/${id}`);
    return response.data;
  },
  
  create: async (periodoId: string, turmaRequest: TurmaRequest): Promise<Turma> => {
    const response = await api.post(`/Periodo/${periodoId}/Turma/Cadastrar`, turmaRequest);
    return response.data;
  },
  
  update: async (id: string, turma: TurmaRequest): Promise<Turma> => {
    const response = await api.put(`/Turma/${id}/Editar`, turma);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/Turma/${id}/Deletar`);
  },
};

export const disciplinaService = {
  getByTurma: async (turmaId: string): Promise<Disciplina[]> => {
    const response = await api.get(`/Turma/${turmaId}/Disciplina/Listar`);
    return response.data;
  },

  getByProfessor: async (professorId: number): Promise<ProfessorDisciplina> => {
    const response = await api.get(`/Professor/${professorId}/Disciplinas`);
    return response.data;
  },

  getById: async (id: number): Promise<Disciplina> => {
    const response = await api.get(`/Disciplina/${id}`);
    return response.data;
  },

  create: async (disciplinaRequest: DisciplinaRequest): Promise<Disciplina> => {
    const response = await api.post('/Disciplina', disciplinaRequest);
    return response.data;
  },

  update: async (id: number, disciplina: Disciplina): Promise<Disciplina> => {
    const response = await api.put(`/Disciplina/${id}`, disciplina);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/Disciplina/${id}/Deletar`);
  },
};

export const periodoService = {
  getAll: async (): Promise<Periodo[]> => {
    const response = await api.get('/Periodo/Listar');
    return response.data;
  },
  
  getById: async (id: string): Promise<Periodo> => {
    const response = await api.get(`/Periodo/${id}`);
    return response.data;
  },
  
  create: async (periodo: Periodo): Promise<Periodo> => {
    const response = await api.post('/Periodo/Cadastrar', periodo);
    return response.data;
  },
  
  update: async (id: string, periodo: Periodo): Promise<Periodo> => {
    const response = await api.put(`/Periodo/${id}/Editar`, periodo);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/Periodo/${id}/Deletar`);
  },
};

export const laboratorioService = {
  getAll: async (): Promise<Laboratorio[]> => {
    const response = await api.get('/laboratorio');
    return response.data;
  },
  
  getById: async (id: string): Promise<Laboratorio> => {
    const response = await api.get(`/laboratorio/${id}`);
    return response.data;
  },
  
  create: async (laboratorio: LaboratorioRequest): Promise<Laboratorio> => {
    const response = await api.post('/laboratorio', laboratorio);
    return response.data;
  },
  
  update: async (id: string, laboratorio: LaboratorioRequest): Promise<Laboratorio> => {
    const response = await api.put(`/laboratorio/${id}`, laboratorio);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/laboratorio/${id}`);
  },
};

export const horarioService = {
  getAll: async (): Promise<Horario[]> => {
    const response = await api.get('/Horario/Listar');
    return response.data;
  },
  
  getById: async (id: number): Promise<Horario> => {
    const response = await api.get(`/Horario/${id}`);
    return response.data;
  },
  
  create: async (periodoId: string, horario: Horario): Promise<Horario> => {
    const response = await api.post(`/Periodo/${periodoId}/Horario/Cadastrar`, horario);
    return response.data;
  },
  
  update: async (id: number, horario: Horario): Promise<Horario> => {
    const response = await api.put(`/Horario/${id}`, horario);
    return response.data;
  },
  
  delete: async (id: number | string): Promise<void> => {
    await api.delete(`/Horario/${id}/Deletar`);
  },
};

export const agendaService = {
  getAll: async (): Promise<Agenda[]> => {
    const response = await api.get('/Agenda/Listar');
    return response.data;
  },
  
  getById: async (id: number): Promise<Agenda> => {
    const response = await api.get(`/Agenda/${id}`);
    return response.data;
  },
  
  getByProfessor: async (professorId: number): Promise<Agenda[]> => {
    const response = await api.get(`/Professor/${professorId}/Agenda/Listar`);
    return response.data;
  },
  
  create: async (agenda: Agenda): Promise<Agenda> => {
    const response = await api.post('/Agenda/Cadastrar', agenda);
    return response.data;
  },

  createBatch: async (agendas: AgendaRequest[]): Promise<Agenda[]> => {
    const response = await api.post('/Agenda/Cadastrar', agendas);
    return response.data;
  },
  
  update: async (id: number, agenda: Agenda): Promise<Agenda> => {
    const response = await api.put(`/Agenda/${id}`, agenda);
    return response.data;
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/Agenda/${id}/Deletar`);
  },
};

export const gradeService = {
  getAll: async (): Promise<Grade[]> => {
    const response = await api.get('/Grade/Listar');
    return response.data;
  },

  getAllByTipo: async (tipo: number): Promise<Grade[]> => {
    const response = await api.get('/Grade/Listar');
    const grades = response.data;
    return grades.filter((grade: Grade) => grade.tipo === tipo);
  },
  
  getById: async (id: string): Promise<Grade> => {
    const response = await api.get(`/Grade/${id}/Carregar`);
    return response.data;
  },
  
  create: async (grade: Grade): Promise<Grade> => {
    const response = await api.post('/Grade/Cadastrar', grade);
    return response.data;
  },
  
  update: async (id: string, grade: Grade): Promise<void> => {
    await api.put(`/Grade/${id}/Editar`, grade);
  },

  substituirProfessor: async (gradeId: string, request: SubstituirProfessorRequest): Promise<SubstituirProfessorResponse> => {
    const response = await api.put(`/Grade/${gradeId}/SubstituirProfessor`, request);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/Grade/${id}/Deletar`);
  },
  
  gerar: async (periodoId: string, gradeRequest: GradeRequest): Promise<GradeGenerateResponse> => {
    // Criar uma instância específica com timeout estendido para geração de grade (5 minutos)
    const apiWithExtendedTimeout = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 300000, // 5 minutos para operações longas
      withCredentials: false,
    });

    // Adicionar interceptors da instância principal
    apiWithExtendedTimeout.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    apiWithExtendedTimeout.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          console.warn('Timeout na geração de grade - operação pode levar até 5 minutos');
          return Promise.reject(new Error('Tempo limite da geração de grade excedido (5 minutos)'));
        }
        
        if (error.code === 'ERR_NETWORK' || !error.response) {
          console.warn('Servidor indisponível durante geração de grade');
          return Promise.reject(new Error('Servidor indisponível'));
        }
        
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }
    );

    const response = await apiWithExtendedTimeout.post(`/Periodo/${periodoId}/Grade/Gerar`, gradeRequest);
    return response.data;
  },

  verificarStatus: async (taskId: string): Promise<GradeStatusResponse> => {
    const response = await api.get(`/Grade/Gerar/Status/${taskId}`);
    return response.data;
  },

  gerarParcial: async (gradeId: string): Promise<string> => {
    // Criar uma instância específica com timeout estendido para geração de grade parcial (10 minutos)
    const apiWithExtendedTimeout = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 600000, // 10 minutos para grade parcial
      withCredentials: false,
    });

    // Adicionar interceptors da instância principal
    apiWithExtendedTimeout.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    apiWithExtendedTimeout.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          console.warn('Timeout na geração de grade parcial - operação pode levar até 10 minutos');
          return Promise.reject(new Error('Tempo limite da geração de grade parcial excedido (10 minutos)'));
        }
        
        if (error.code === 'ERR_NETWORK' || !error.response) {
          console.warn('Servidor indisponível durante geração de grade parcial');
          return Promise.reject(new Error('Servidor indisponível'));
        }
        
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }
    );

    const response = await apiWithExtendedTimeout.get(`/GradeParcial/Gerar/${gradeId}`);
    return response.data;
  },

  gerarGradeLaboratorio: async (gradeId: string): Promise<GradeLaboratorioGerarResponse> => {
    const response = await api.post(`/GradeLaboratorio/Grade/${gradeId}`);
    return response.data;
  },

  aplicarRemanejamentoLaboratorio: async (
    gradeId: string,
    trocas: Array<{ gradeHorarioIdA: string; gradeHorarioIdB: string }>
  ): Promise<GradeLaboratorioGerarResponse> => {
    const response = await api.put(`/GradeLaboratorio/Grade/${gradeId}/AplicarRemanejamento`, { trocas });
    return response.data;
  },

  listarGradesLaboratorio: async (): Promise<Grade[]> => {
    const response = await api.get('/GradeLaboratorio/Listar');
    return response.data;
  },

  getGradeLaboratorioById: async (id: string): Promise<GradeLaboratorio> => {
    const response = await api.get(`/GradeLaboratorio/${id}`);
    return response.data;
  },

  deleteGradeLaboratorio: async (id: string): Promise<void> => {
    await api.delete(`/GradeLaboratorio/${id}`);
  },

  updateGradeLaboratorio: async (modifiedItems: any[]): Promise<void> => {
    await api.put('/GradeLaboratorio', modifiedItems);
  },

  checkCards: async (gradeLaboratorioId: string, disciplinaId: string, horarioId: string): Promise<boolean> => {
    const response = await api.post('/GradeLaboratorio/CheckCards', {
      gradeLaboratorioId,
      disciplinaId,
      horarioId
    });
    return response.data;
  },
};

export const agendaProfessorService = {
  getByAgenda: async (agendaId: string): Promise<AgendaProfessor[]> => {
    const response = await api.get(`/Agenda/${agendaId}/AgendaProfessor/Listar`);
    return response.data;
  },
  
  updateDisponibilidade: async (id: string, request: AgendaProfessorRequest): Promise<void> => {
    await api.put(`/AgendaProfessor/${id}/Editar`, request);
  },
  
  verificarDisponibilidade: async (professorId: string, horarioId: string): Promise<{ disponivel: boolean }> => {
    const response = await api.post('/AgendaProfessor/Verificar', {
      professorId,
      horarioId
    });
    return response.data;
  },
};

export const professorAgendaService = {
  getAgendasByProfessor: async (professorId: number): Promise<Agenda[]> => {
    const response = await api.get(`/Professor/${professorId}/Agenda/Listar`);
    return response.data;
  },
};

export const configuracaoGradeService = {
  buscar: async (): Promise<ConfiguracaoGrade> => {
    const response = await api.get('/ConfiguracaoGrade/Buscar');
    return response.data;
  },

  atualizar: async (configuracao: ConfiguracaoGrade): Promise<void> => {
    await api.put('/ConfiguracaoGrade/Atualizar', configuracao);
  },
};

export const agendaTurmaService = {
  getByTurma: async (turmaId: string): Promise<AgendaTurma[]> => {
    const response = await api.get(`/Turma/${turmaId}/Agenda/Listar`);
    return response.data;
  },
};

export const agendaTurmaHorarioService = {
  getByAgenda: async (agendaId: string): Promise<AgendaTurmaHorario[]> => {
    const response = await api.get(`/AgendaTurma/${agendaId}/AgendaTurmaHorarios/Listar`);
    return response.data;
  },

  updateDisponibilidade: async (id: string, request: AgendaTurmaHorarioRequest): Promise<AgendaTurmaHorario> => {
    const response = await api.put(`/AgendaTurmaHorarios/${id}/Editar`, request);
    return response.data;
  },
};

export const laboratorioDisciplinaService = {
  getByDisciplina: async (disciplinaId: string): Promise<LaboratorioDisciplina | null> => {
    try {
      const response = await api.get(`/laboratoriodisciplina/disciplina/${disciplinaId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 204) {
        return null;
      }
      throw error;
    }
  },

  getByLaboratorio: async (laboratorioId: string): Promise<LaboratorioDisciplina[]> => {
    try {
      const response = await api.get(`/laboratoriodisciplina/laboratorio/${laboratorioId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 204) {
        return [];
      }
      throw error;
    }
  },

  create: async (request: LaboratorioDisciplinaRequest): Promise<LaboratorioDisciplina> => {
    const response = await api.post('/laboratoriodisciplina', request);
    return response.data;
  },

  update: async (id: string, request: LaboratorioDisciplinaRequest): Promise<LaboratorioDisciplina> => {
    const response = await api.put(`/laboratoriodisciplina/${id}`, request);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/laboratoriodisciplina/${id}`);
  },
};

// Interface para dados do Dashboard
export interface UserProfile {
  usuarioId: string;
  nome: string;
  email: string;
  administrador: boolean;
  empresaId: string;
  razao: string;
  cnpj: string;
  vencimento: string;
  valor: number;
}

export interface Empresa {
  id?: string;
  razao: string;
  cnpj: string;
  vencimento: string;
  valor: number;
  ativo: boolean;
}

export interface EmpresaRequest {
  razao: string;
  cnpj: string;
  vencimento: string;
  valor: number;
}

export interface EmpresaUpdateRequest {
  razao: string;
  cnpj: string;
  vencimento: string;
  valor: number;
  ativo: boolean;
}

export interface Usuario {
  id?: string;
  nome: string;
  email: string;
  empresaId: string;
  razao: string;
  administrador: boolean;
}

export interface UsuarioRequest {
  nome: string;
  email: string;
  senha: string;
}

export interface DashboardStats {
  totalProfessores: number;
  totalTurmas: number;
  totalDisciplinas: number;
  totalPeriodos: number;
  totalGradeGeradas: number;
}

export const userService = {
  getProfile: async (): Promise<UserProfile> => {
    const response = await api.get('/Usuario/Perfil');
    return response.data;
  },
  
  changePassword: async (senhaAtual: string, novaSenha: string): Promise<void> => {
    await api.put('/Usuario/Alterarsenha', {
      senhaAtual,
      novaSenha
    });
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/Usuario/${id}/Deletar`);
  },
};

export const empresaService = {
  getAll: async (): Promise<Empresa[]> => {
    const response = await api.get('/Empresa/Listar');
    return response.data;
  },

  create: async (empresa: EmpresaRequest): Promise<Empresa> => {
    const response = await api.post('/Empresa/Criar', empresa);
    return response.data;
  },

  update: async (id: string, empresa: EmpresaUpdateRequest): Promise<Empresa> => {
    const response = await api.put(`/Empresa/${id}/Atualizar`, empresa);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/Empresa/${id}/Deletar`);
  },

  // Métodos para usuários da empresa
  createUser: async (empresaId: string, usuario: UsuarioRequest): Promise<Usuario> => {
    const response = await api.post(`/Empresa/${empresaId}/Usuario/Criar`, usuario);
    return response.data;
  },

  getUsers: async (empresaId: string): Promise<Usuario[]> => {
    const response = await api.get(`/Empresa/${empresaId}/Usuario/Listar`);
    return response.data;
  },
};

export const dashboardService = {
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/Dashboard/Listar');
    return response.data;
  },
};

// Serviços específicos baseados no projeto antigo
export const apiServices = {
  // Função para buscar disciplinas por turma
  getDisciplinasByTurma: async (turmaId: string) => {
    const response = await api.get(`/Turma/${turmaId}/Disciplina/Listar`);
    return response.data;
  },
  
  // Horários por período
  getHorariosByPeriodo: async (periodoId: string) => {
    const response = await api.get(`/Periodo/${periodoId}/Horario/Listar`);
    return response.data;
  },
  
  // Cadastrar horários para múltiplos dias
  cadastrarHorarios: async (periodoId: string, horarios: Array<{diaSemana: number, inicio: string, fim: string}>) => {
    const response = await api.post(`/Periodo/${periodoId}/Horario/Cadastrar`, horarios);
    return response.data;
  },
};

// Interfaces para Agendas de Laboratórios
export interface AgendaLaboratorio {
  id?: string;
  nome: string;
  turno: string;
  dataInicio: string;
  dataFim: string;
  periodo: {
    id: string;
    nome: string;
  };
  laboratorio: {
    id: string;
    nome: string;
  };
}

export interface AgendaLaboratorioHorario {
  id?: string;
  horario: {
    id: string;
    diaSemana: number;
    posicaoDia: number;
    inicio: string;
    fim: string;
    periodoId: string;
  };
  disponivel: boolean;
  indisponibilidadeMotivo: string;
}

export interface AgendaLaboratorioHorarioRequest {
  disponivel: boolean;
  indisponibilidadeMotivo?: string;
}

// Serviços para Agendas de Laboratórios
export const agendaLaboratorioService = {
  getByLaboratorio: async (laboratorioId: string): Promise<AgendaLaboratorio[]> => {
    const response = await api.get(`/Laboratorio/${laboratorioId}/Agendas`);
    return response.data;
  },
};

export const agendaLaboratorioHorarioService = {
  getByAgenda: async (agendaId: string): Promise<AgendaLaboratorioHorario[]> => {
    const response = await api.get(`/AgendaLaboratorio/${agendaId}`);
    return response.data;
  },

  updateDisponibilidade: async (id: string, request: AgendaLaboratorioHorarioRequest): Promise<AgendaLaboratorioHorario> => {
    const response = await api.put(`/AgendaLaboratorioHorarios/${id}`, request);
    return response.data;
  },
};

export default api;